import { TrainingExample } from '../types';
import { saveFileToProject, getProjectHandle } from './fileSystemService';

const DB_NAME = 'SST_Vault_DB';
const STORE_NAME = 'ground_truth';
const DB_VERSION = 1;
const REMOTE_API = './data_handler.php';

// ─── Verifier tracking ──────────────────────────────────────────────────────────
// Persistent per-browser ID used to attribute verifications for traceability.
// Not a user ID — survives page refreshes on the same machine but not across browsers.
const VERIFIER_ID_KEY = 'gea_ndt_verifier_id';
const getVerifierId = (): string => {
  try {
    let id = localStorage.getItem(VERIFIER_ID_KEY);
    if (!id) {
      // Small random ID, timestamped so it's somewhat unique across the whole fleet
      id = 'M' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8).toUpperCase();
      localStorage.setItem(VERIFIER_ID_KEY, id);
    }
    return id;
  } catch (e) {
    return 'M-ANON';
  }
};

// ─── Clean-break marker ─────────────────────────────────────────────────────────
// When NAS becomes source of truth for images, we wipe legacy IndexedDB entries
// that still carry base64 images (they'll be re-fetched from NAS, which will serve
// them via .jpg URLs). Runs once per browser.
const UPGRADE_FLAG_KEY = 'gea_ndt_vault_nas_upgrade_v1';
const hasRunNasUpgrade = (): boolean => {
  try { return localStorage.getItem(UPGRADE_FLAG_KEY) === '1'; } catch { return false; }
};
const markNasUpgradeDone = (): void => {
  try { localStorage.setItem(UPGRADE_FLAG_KEY, '1'); } catch { /* ignore */ }
};

// Sanitize a defect type into a safe filename token (e.g. "Repair Site" -> "REPAIR_SITE")
const slugifyType = (type: string | undefined | null): string => {
  if (!type) return 'UNKNOWN';
  return type.toString().toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'UNKNOWN';
};

// Split one TrainingExample with N findings into N entries, each with one finding.
// - Each split gets a unique timestamp (base + index) so they coexist in IndexedDB.
// - sourceGroup retains the original timestamp so we can group them back later.
// - splitVersion marks them as already-split so migration won't re-split them.
// - Returns [] for entries with zero findings (verified-clean entries are skipped).
const splitEntryByFinding = (entry: TrainingExample): TrainingExample[] => {
  const findings = entry.analysis?.findings || [];
  if (findings.length === 0) return [];
  const baseTs = entry.timestamp || Date.now();
  const sourceGroup = entry.sourceGroup ?? baseTs;
  return findings.map((f, idx) => ({
    ...entry,
    timestamp: baseTs + idx,
    sourceGroup,
    splitVersion: 1,
    analysis: {
      ...entry.analysis,
      findings: [f],
    },
  }));
};

const openDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'timestamp' });
      }
    };

    request.onsuccess = (event) => {
      resolve((event.target as IDBOpenDBRequest).result);
    };

    request.onerror = (event) => {
      reject((event.target as IDBOpenDBRequest).error);
    };
  });
};

// Disk-only writer used by migration. Does NOT touch IndexedDB or remote sync.
const writeSingleEntryToDiskOnly = async (entry: TrainingExample, dupIndex: number = 0): Promise<void> => {
  if (!getProjectHandle()) return;
  try {
    if (!entry.image) return;
    const base64Data = entry.image.split(',')[1];
    if (!base64Data) return;
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) byteNumbers[i] = byteCharacters.charCodeAt(i);
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'image/jpeg' });
    const metaBlob = new Blob([JSON.stringify(entry, null, 2)], { type: 'application/json' });

    const finding = entry.analysis?.findings?.[0];
    const typeSlug = slugifyType(finding?.type);
    const groupTs = entry.sourceGroup ?? entry.timestamp ?? Date.now();
    const dupSuffix = dupIndex > 0 ? `_${dupIndex}` : '';
    const fileName = `verified_${groupTs}_${typeSlug}${dupSuffix}`;

    await saveFileToProject('02_Verified_Findings', `${fileName}.jpg`, blob);
    await saveFileToProject('02_Verified_Findings', `${fileName}.json`, metaBlob);
    await saveFileToProject('06_Ground_Truth', `${fileName}.jpg`, blob);
    await saveFileToProject('06_Ground_Truth', `${fileName}.json`, metaBlob);
  } catch (e) {
    console.warn('[VaultService] Migration disk-write failed:', e);
  }
};

// Internal: write ONE entry (already representing a single finding) to all targets.
// Filename uses defect type for traceability; appended index disambiguates duplicate types
// within the same source group (e.g. two Dents from one image -> _DENT_1, _DENT_2).
const writeSingleEntry = async (entry: TrainingExample, dupIndex: number = 0): Promise<void> => {
  const isLocal = !!getProjectHandle();

  // 1. Browser/NAS Save (Only if NOT in local folder mode)
  if (!isLocal) {
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put(entry);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    try {
      fetch(REMOTE_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'ground_truth', data: entry })
      }).catch(err => console.warn("Remote Vault sync failed:", err));
    } catch (e) { }
  }

  // 2. Local Project Folder Save (Mandatory if active)
  try {
    if (entry.image) {
      const base64Data = entry.image.split(',')[1];
      if (!base64Data) {
        console.error('[VaultService] Image is not a valid data URL — cannot split base64. Image starts with:', entry.image.substring(0, 50));
        return;
      }
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/jpeg' });
      const metaBlob = new Blob([JSON.stringify(entry, null, 2)], { type: 'application/json' });

      const finding = entry.analysis?.findings?.[0];
      const typeSlug = slugifyType(finding?.type);
      const groupTs = entry.sourceGroup ?? entry.timestamp ?? Date.now();
      const dupSuffix = dupIndex > 0 ? `_${dupIndex}` : '';
      const fileName = `verified_${groupTs}_${typeSlug}${dupSuffix}`;

      // A: Save to Official Verified Results (02_)
      const saved02 = await saveFileToProject('02_Verified_Findings', `${fileName}.jpg`, blob);
      await saveFileToProject('02_Verified_Findings', `${fileName}.json`, metaBlob);
      console.log('[VaultService] Saved to 02_Verified_Findings:', fileName, '| success:', saved02);

      // B: Save to AI Training Ground Truth (06_)
      await saveFileToProject('06_Ground_Truth', `${fileName}.jpg`, blob);
      await saveFileToProject('06_Ground_Truth', `${fileName}.json`, metaBlob);
    } else {
      console.warn('[VaultService] No image in entry — skipping local disk save. Findings:', entry.analysis?.findings?.length);
    }
  } catch (e) {
    console.error("Local vault save failed:", e);
  }
};

export const saveEntryToVault = async (entry: TrainingExample): Promise<void> => {
  const findingsCount = entry.analysis?.findings?.length ?? 0;
  console.log('[VaultService] saveEntryToVault | hasImage:', !!entry.image, '| findings:', findingsCount);

  // Verified-clean entries (zero findings) are skipped per current spec.
  if (findingsCount === 0) {
    console.log('[VaultService] Entry has no findings — skipping (verified-clean entries are not stored).');
    return;
  }

  // Split N findings into N entries (one defect per training sample).
  const splits = splitEntryByFinding(entry);

  // Attribution: stamp verifier ID + time on each split.
  const verifierId = getVerifierId();
  const verifiedAt = Date.now();
  const stampedSplits = splits.map(s => ({ ...s, lastVerifiedBy: verifierId, lastVerifiedAt: verifiedAt }));

  // Track duplicate types within the same group so filenames don't collide.
  const typeCounts: Record<string, number> = {};
  for (const split of stampedSplits) {
    const t = slugifyType(split.analysis?.findings?.[0]?.type);
    const dupIndex = typeCounts[t] || 0;
    typeCounts[t] = dupIndex + 1;
    await writeSingleEntry(split, dupIndex);
  }
};


// Run-once migration: split existing multi-finding entries into per-finding entries.
// - Entries already marked splitVersion >= 1 are left alone.
// - Entries with 0 findings: deleted (verified-clean entries are no longer kept per spec).
// - Entries with 1 finding: marked splitVersion=1 in place (no actual split needed).
// - Entries with N>1 findings: replaced by N split entries; the original is deleted from IndexedDB.
//   Local files for the old entry are LEFT IN PLACE (per spec — old + new coexist on disk).
const migrateVaultIfNeeded = async (db: IDBDatabase): Promise<void> => {
  const allEntries: TrainingExample[] = await new Promise((resolve, reject) => {
    const tx = db.transaction([STORE_NAME], 'readonly');
    const req = tx.objectStore(STORE_NAME).getAll();
    req.onsuccess = () => resolve(req.result as TrainingExample[]);
    req.onerror = () => reject(req.error);
  });

  const needsWork = allEntries.filter(e => (e.splitVersion ?? 0) < 1);
  if (needsWork.length === 0) return;

  console.log(`[VaultService] Migration: splitting ${needsWork.length} legacy entries into per-defect samples.`);

  // Plan all writes/deletes first, then apply in one transaction so partial failure can't corrupt state.
  const writes: TrainingExample[] = [];
  const deletes: number[] = [];

  for (const entry of needsWork) {
    const findings = entry.analysis?.findings || [];
    if (findings.length === 0) {
      // Verified-clean — drop per spec
      deletes.push(entry.timestamp);
    } else if (findings.length === 1) {
      // Already single-finding; just stamp the version marker
      writes.push({ ...entry, splitVersion: 1, sourceGroup: entry.sourceGroup ?? entry.timestamp });
    } else {
      // True split
      const splits = splitEntryByFinding(entry);
      writes.push(...splits);
      deletes.push(entry.timestamp);
    }
  }

  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction([STORE_NAME], 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    for (const ts of deletes) store.delete(ts);
    for (const e of writes) store.put(e);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });

  // Write split files to local disk (best-effort; old single-finding files are left in place per spec).
  // Only triggered when a project handle is active; skipped for browser-only mode.
  if (getProjectHandle()) {
    const splitsToWriteToDisk = writes.filter(e => (e.analysis?.findings?.length ?? 0) === 1 && e.image);
    // Group by sourceGroup to handle duplicate-type filenames within the same group
    const byGroup: Record<number, TrainingExample[]> = {};
    for (const s of splitsToWriteToDisk) {
      const g = s.sourceGroup ?? s.timestamp;
      (byGroup[g] = byGroup[g] || []).push(s);
    }
    for (const group of Object.values(byGroup)) {
      const typeCounts: Record<string, number> = {};
      for (const split of group) {
        const t = slugifyType(split.analysis?.findings?.[0]?.type);
        const dupIndex = typeCounts[t] || 0;
        typeCounts[t] = dupIndex + 1;
        try { await writeSingleEntryToDiskOnly(split, dupIndex); }
        catch (e) { console.warn('[VaultService] Migration disk-write failed for entry', split.timestamp, e); }
      }
    }
  }

  console.log(`[VaultService] Migration complete. Wrote ${writes.length} split entries, removed ${deletes.length} legacy entries.`);
};

export const getVaultEntries = async (): Promise<TrainingExample[]> => {
  const db = await openDB();

  // Clean-break upgrade: first load after the NAS-image rollout, wipe legacy local entries.
  // They carry base64 images and will be re-fetched from NAS as metadata+URL shortly below.
  if (!hasRunNasUpgrade()) {
    try {
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction([STORE_NAME], 'readwrite');
        tx.objectStore(STORE_NAME).clear();
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
      });
      markNasUpgradeDone();
      console.log('[VaultService] NAS upgrade: IndexedDB wiped, NAS is now the source of truth.');
    } catch (e) {
      console.warn('[VaultService] NAS upgrade wipe failed:', e);
    }
  }

  // Legacy (now no-op on upgraded browsers) — split any multi-finding entries still around.
  try { await migrateVaultIfNeeded(db); }
  catch (e) { console.warn('[VaultService] Legacy migration skipped:', e); }

  // Pull latest from NAS — this is the authoritative copy.
  // Proper sync: if the NAS fetch succeeds, IndexedDB becomes a mirror of NAS (entries
  // removed server-side vanish locally). If the fetch fails (offline, CORS, etc.), we
  // leave IndexedDB untouched so the user still sees the last-known cache.
  let nasReachable = false;
  let remoteEntries: TrainingExample[] = [];
  try {
    const resp = await fetch(REMOTE_API);
    const json = await resp.json();
    if (json && json.status === 'online' && Array.isArray(json.vault)) {
      remoteEntries = json.vault;
      nasReachable = true;
    }
  } catch (e) {
    console.warn('[VaultService] Could not sync remote vault, using local cache.');
  }

  if (nasReachable) {
    // Reconcile: clear then repopulate in ONE transaction so no partial-state window.
    // Remote entries get splitVersion stamped so the JS-side migration never re-runs on them.
    try {
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction([STORE_NAME], 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        store.clear();
        for (const entry of remoteEntries) {
          if (entry && typeof entry.timestamp === 'number') {
            const normalized: TrainingExample = {
              ...entry,
              splitVersion: entry.splitVersion ?? 1,
              sourceGroup: entry.sourceGroup ?? entry.timestamp,
            };
            store.put(normalized);
          }
        }
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
      });
      console.log(`[VaultService] Synced ${remoteEntries.length} entries from NAS (cache reconciled).`);
    } catch (e) {
      console.warn('[VaultService] Cache reconciliation failed:', e);
    }
  }

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAll();

    request.onsuccess = () => {
      const results = (request.result as TrainingExample[]).sort((a, b) => b.timestamp - a.timestamp);
      resolve(results);
    };
    request.onerror = () => reject(request.error);
  });
};

export const deleteVaultEntry = async (timestamp: number): Promise<void> => {
  const db = await openDB();

  // 1. IndexedDB delete
  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(timestamp);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });

  // 2. Local project folder delete (if active) — removes both jpg + json from 02_ and 06_
  // Tries the new naming scheme (verified_<group>_<TYPE>) and the legacy single-file scheme (verified_<ts>).
  try {
    const projectHandle = getProjectHandle();
    if (projectHandle) {
      const targets = ['02_Verified_Findings', '06_Ground_Truth'];
      for (const folderName of targets) {
        try {
          const folder = await projectHandle.getDirectoryHandle(folderName, { create: false });
          // Best-effort: scan directory for any file matching this timestamp's split or legacy pattern.
          const matchPrefixes = [`verified_${timestamp}_`, `verified_${timestamp}.`];
          // @ts-ignore — values() exists on FileSystemDirectoryHandle
          for await (const handle of folder.values()) {
            const name: string = handle.name;
            if (matchPrefixes.some(p => name.startsWith(p))) {
              try { await folder.removeEntry(name); }
              catch (e) { /* ignore */ }
            }
          }
        } catch (e) { /* folder may not exist; ignore */ }
      }
    }
  } catch (e) {
    console.warn('[VaultService] Local file delete failed:', e);
  }

  // 3. Remote vault sync delete (best-effort, fire-and-forget)
  try {
    fetch(REMOTE_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'ground_truth_delete', data: { timestamp } })
    }).catch(err => console.warn('Remote Vault delete sync failed:', err));
  } catch (e) { }
};

/**
 * Update a vault entry via evict-and-create semantics (Option B).
 *
 * - Deletes the old entry (by oldTimestamp) from IndexedDB + NAS (awaited)
 * - Creates a new entry with the supplied payload (new timestamp, attribution fields)
 * - Both NAS round-trips are awaited so the caller can safely reload the vault
 *   immediately afterwards and see the updated state. (saveEntryToVault and
 *   deleteVaultEntry fire-and-forget their NAS calls, which would race the reload.)
 * - Local project files are intentionally NOT touched (per spec — NAS is source of truth)
 */
export const updateVaultEntry = async (oldTimestamp: number, nextEntry: TrainingExample): Promise<void> => {
  const verifierId = getVerifierId();
  const stampedEntry: TrainingExample = {
    ...nextEntry,
    lastVerifiedBy: verifierId,
    lastVerifiedAt: Date.now(),
  };

  const db = await openDB();

  // 1a. Remove the old entry from IndexedDB so the next read doesn't return it.
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction([STORE_NAME], 'readwrite');
      tx.objectStore(STORE_NAME).delete(oldTimestamp);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (e) {
    console.warn('[VaultService] IndexedDB delete of old entry failed:', e);
  }

  // 1b. Delete on NAS (AWAITED — so the subsequent reload sees the old entry gone).
  try {
    const resp = await fetch(REMOTE_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'ground_truth_delete', data: { timestamp: oldTimestamp } })
    });
    if (!resp.ok) console.warn('[VaultService] NAS delete returned', resp.status);
  } catch (e) {
    console.warn('[VaultService] NAS delete failed:', e);
  }

  // 2a. Insert the new entry into IndexedDB.
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction([STORE_NAME], 'readwrite');
      tx.objectStore(STORE_NAME).put(stampedEntry);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (e) {
    console.warn('[VaultService] IndexedDB put of new entry failed:', e);
  }

  // 2b. POST the new entry to NAS (AWAITED — PHP writes .jpg + .json + updates storage_vault.json).
  try {
    const resp = await fetch(REMOTE_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'ground_truth', data: stampedEntry })
    });
    if (!resp.ok) throw new Error(`NAS save returned ${resp.status}`);
  } catch (e) {
    console.error('[VaultService] NAS save of new entry failed:', e);
    throw e;
  }
};

